import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { first, map, size, slice } from 'es-toolkit/compat';
import React from 'react';

import SlideThumbnailImage from 'components/StudyDashboard/ProceduresPage/ProcedureCard/Carousel/SlideThumbnailImage';
import { Slide } from 'interfaces/slide';

const inlineThumbnailSx = {
  position: 'relative',
  width: 32,
  height: 32,
  overflow: 'hidden',
  borderRadius: 1.25,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
  flexShrink: 0,
} as const;

interface SlidePreviewSummaryRowProps {
  slides?: Slide[];
  summaryLabel?: string;
  summaryContent?: React.ReactNode;
  onOpenPreview: (slideId?: string) => void;
}

export const SlidePreviewSummaryRow: React.FC<SlidePreviewSummaryRowProps> = ({
  slides,
  summaryLabel,
  summaryContent,
  onOpenPreview,
}) => {
  const selectedSlide = first(slides);
  const visibleSlides = slice(slides, 0, 3);
  const remainingSlidesCount = Math.max(size(slides) - size(visibleSlides), 0);

  if (!selectedSlide) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
      {map(visibleSlides, (slide) => (
        <Tooltip key={slide.id} title={`Preview ${slide.id}`}>
          <ButtonBase
            onClick={() => onOpenPreview(slide.id)}
            aria-label={`Preview slide: ${slide.id}`}
            sx={{ borderRadius: 1.25 }}
          >
            <Box sx={inlineThumbnailSx}>
              <SlideThumbnailImage slide={slide} />
            </Box>
          </ButtonBase>
        </Tooltip>
      ))}

      {remainingSlidesCount > 0 && (
        <ButtonBase
          onClick={() => onOpenPreview()}
          aria-label={`Preview ${remainingSlidesCount} more slides`}
          sx={{
            minWidth: 32,
            height: 32,
            px: 0.75,
            borderRadius: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>
            +{remainingSlidesCount}
          </Typography>
        </ButtonBase>
      )}

      {summaryContent ? (
        <Box sx={{ minWidth: 0, maxWidth: '100%', display: 'flex', alignItems: 'center' }}>{summaryContent}</Box>
      ) : (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
          {summaryLabel || (size(slides) === 1 ? selectedSlide.id : `${size(slides)} slides`)}
        </Typography>
      )}
    </Stack>
  );
};
